import { Moment } from 'moment';
import React, { FC, useState } from 'react';
import styled, { css } from 'styled-components';
import { ifNotProp, ifProp } from 'styled-tools';
import { Project } from 'api/project';
import { TimesheetEntryType } from 'api/timesheets';
import { Category } from 'api/timesheets/categories';
import { EnabledSourcesConfiguration } from 'api/timesheets/configuration/configuration';
import { Option } from 'components/atoms/select';
import { ProjectNameCell } from 'components/organisms/timesheets/common/cells/project-name-cell';
import { TaskNameCell } from 'components/organisms/timesheets/common/cells/task-name-cell';
import { getMinutes } from 'components/organisms/timesheets/common/time-entry-utils';
import { TimesheetTableTimeEntry, TimesheetWeeklyRowTask } from '../types';
import { TimeEntryCell } from './time-entry-cell';

enum CellType {
  ProjectCell = 'project-selection-cell',
  TaskCell = 'task-selection-cell',
  TimeCell = 'time-entry-cell',
}

const Cell = styled.td<{ isActive; isError }>`
  position: relative;

  &:before {
    position: absolute;
    content: '';
    top: 0px;
    bottom: -1px;
    left: 0px;
    right: 0px;
  }
  ${ifNotProp(
    'isError',
    css`
      &:hover,
      &:focus-within {
        background-color: var(--blue-10) !important;
      }
      &:focus-within:before {
        border: 2px solid var(--blue-70);
      }
    `,
  )};

  ${ifProp(
    'isActive',
    css`
      background-color: var(--blue-10) !important;
      &:before {
        border: 2px solid var(--blue-70);
      }
    `,
  )};

  ${ifProp(
    'isError',
    css`
      background-color: var(--red-10) !important;
      &:before {
        border: 2px solid var(--red-70);
      }
    `,
  )};
`;

const StyledCell = styled.td<{
  isActive: boolean;
  minutes: number;
  isTaskCell?: boolean;
}>`
  position: relative;
  border-right: ${({ isTaskCell }) =>
    isTaskCell && '1px solid var(--table-border-color)'};
  &:before {
    position: absolute;
    top: 0px;
    bottom: -1px;
    left: 0px;
    right: 0px;
  }
  &:hover {
    background-color: ${(props) =>
      props.minutes === 0 && 'var(--blue-10)'} !important;
  }

  ${ifProp(
    'isActive',
    css`
      background-color: var(--blue-10) !important;
      &::before {
        content: '';
        border: 2px solid var(--blue-70);
      }
    `,
  )};
`;

interface CommonProps {
  task: TimesheetWeeklyRowTask;
  timesheetEntriesConfig: EnabledSourcesConfiguration;
  days: Record<string, TimesheetTableTimeEntry[]>;
}
interface TimeEntryCellProps extends CommonProps {
  type: CellType.TimeCell;
  timeEntries: TimesheetTableTimeEntry[];
  timeEntryCategories: Category[];
  day: Moment;
  canEditTimeCell: boolean;
  updateDayEntry: (entryAt: string, timeEntry: TimesheetTableTimeEntry) => void;
  deleteDayEntry: (entryAt: string, timeEntryId: number) => void;
  addDayEntry: (entryAt: string) => void;
  mandateCategory: boolean;
}

interface TaskSelectionEntryProps extends CommonProps {
  type: CellType.TaskCell;
  addEntityToProject: (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
    entity: any,
  ) => void;
  addActivityToProject: (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
    activityName: string,
  ) => void;
  projects: Project[];
}

interface ProjectSelectionEntryProps extends CommonProps {
  type: CellType.ProjectCell;
  projects: Project[];
  projectOptions: Option[];
  changeProject: (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
    projectToBeAdded: Option,
  ) => void;
}

const isDeleted = (entity: TimesheetWeeklyRowTask) => {
  if (entity?.project?.archived || entity?.project?.deleted) {
    return true;
  }
  if (entity.entryType === TimesheetEntryType.PhaseEntry) {
    return entity.projectPhase?.deleted;
  }
  if (entity.entryType === TimesheetEntryType.TaskEntry) {
    return entity.deleted;
  }

  return false;
};

export const EditableCell: FC<
  TimeEntryCellProps | TaskSelectionEntryProps | ProjectSelectionEntryProps
> = (props) => {
  const [isActive, setIsActive] = useState(false);
  const [isError, setIsError] = useState(false);

  const { task, days, timesheetEntriesConfig, type, children, ...restProps } =
    props;

  if (type === CellType.TimeCell) {
    const minutes = getMinutes(days);

    const {
      timeEntries,
      timeEntryCategories,
      day,
      canEditTimeCell,
      updateDayEntry,
      deleteDayEntry,
      addDayEntry,
    } = props;

    const blockEntriesAgainstProject =
      minutes === 0 &&
      timesheetEntriesConfig.PROJECT === false &&
      task.entryType === TimesheetEntryType.ProjectEntry;

    const blockEntriesAgainstNonProject =
      minutes === 0 &&
      task.entryType === TimesheetEntryType.ProjectEntry &&
      (task.project == null || task.project?.id == null);

    return (
      <Cell isActive={isActive} isError={isError} {...restProps}>
        <TimeEntryCell
          onActivate={setIsActive}
          onError={setIsError}
          timeEntries={timeEntries}
          task={task}
          day={day}
          editable={
            !isDeleted(task) &&
            canEditTimeCell &&
            !blockEntriesAgainstProject &&
            !blockEntriesAgainstNonProject
          }
          dontShowPopup={
            blockEntriesAgainstProject || blockEntriesAgainstNonProject
          }
          timeEntryCategories={timeEntryCategories}
          updateDayEntry={updateDayEntry}
          addDayEntry={addDayEntry}
          deleteDayEntry={deleteDayEntry}
          mandateCategory={props.mandateCategory}
        />
      </Cell>
    );
  }

  if (type === CellType.TaskCell) {
    const minutes = getMinutes(days);
    const { addEntityToProject, addActivityToProject, projects } = props;

    return (
      <StyledCell
        isActive={isActive}
        minutes={minutes}
        {...restProps}
        isTaskCell
      >
        <TaskNameCell
          task={task}
          days={days}
          onActivate={setIsActive}
          addEntityToProject={addEntityToProject}
          timesheetEntriesConfig={timesheetEntriesConfig}
          addActivityToProject={addActivityToProject}
          projects={projects}
        />
      </StyledCell>
    );
  }

  if (type === CellType.ProjectCell) {
    const minutes = getMinutes(days);
    const { projects, projectOptions, changeProject } = props;

    return (
      <StyledCell isActive={isActive} minutes={minutes} {...restProps}>
        <ProjectNameCell
          task={task}
          days={days}
          onActivate={setIsActive}
          projects={projects}
          projectOptions={projectOptions}
          changeProject={changeProject}
        />
      </StyledCell>
    );
  }

  return <td {...restProps}>{children}</td>;
};
